import React, { useEffect, useState } from "react";
import { Button, Modal, Upload, message } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  addFile,
  removeStagedFile,
  markFileForDeletion,
  removeExistingFile,
} from "./AttachmentsSlice";
import { deleteAttachment } from "./AttachmentsAPI";
import { FormattedMessage, useIntl } from "react-intl";
import FormType from "features/Enums/FormType";
import OpenNotificationWithIcon from "CommonComponents/ResultNotification/Notification";

const MultiFileUploader = (props) => {
  const { value = [], onChange } = props;
  const dispatch = useDispatch();
  const intl = useIntl();
  const attachments = useSelector((state) => state.Attachments.attachments);
  const [fileList, setFileList] = useState([]);
  const isEmployee = new Set([FormType.Employee.id]).has(props?.identifier);
  const allowMultiple = props?.multipleUpload ?? !isEmployee;

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result?.split(",")[1]);
      reader.onerror = reject;
    });

  useEffect(() => {
    const documents =
      attachments?.[props?.identifier]?.[props?.currentId]?.documents?.filter(
        (doc) => !doc.isThumbnail
      ) || [];

    const mapped = documents.map((doc, i) => ({
      uid: doc.guidFileName || `existing-${i}`,
      name: doc.originalFileName || `file-${i + 1}`,
      status: "done",
      url: `data:application/octet-stream;base64,${doc.base64Content}`,
      isExisting: true,
      id: doc.id,
    }));

    setFileList(mapped);
  }, [attachments, props?.identifier, props?.currentId]);

  const handleUploaderChange = async ({ file, fileList: newList }) => {
    if (file.status === "removed") {
      file.isExisting
        ? dispatch(
            markFileForDeletion({
              identifier: props?.identifier,
              id: props?.currentId,
              fileId: file.id,
            })
          )
        : dispatch(
            removeStagedFile({
              identifier: props?.identifier,
              id: props?.currentId,
              fileName: file.name,
            })
          );
      setFileList(newList.filter((f) => f.uid !== file.uid));
      props.onChange?.(newList.filter((f) => f.uid !== file.uid));
      return;
    }

    if (file.originFileObj) {
      const base64 = await getBase64(file.originFileObj);
      dispatch(
        addFile({
          fileName: file.name,
          base64,
          identifier: props?.identifier,
          id: props?.currentId,
        })
      );

      setFileList([
        ...fileList,
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: `data:application/octet-stream;base64,${base64}`,
        },
      ]);
      onChange?.([
        ...value,
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: `data:application/octet-stream;base64,${base64}`,
        },
      ]);
    }
  };

  const onRemove = async (file) => {
    Modal.confirm({
      title: "Are you sure to delete this file?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        try {
          setFileList(fileList.filter((f) => f.uid !== file.uid));
          onChange?.(value.filter((f) => f.uid !== file.uid));

          await dispatch(deleteAttachment([file.id]));
          dispatch(
            markFileForDeletion({
              identifier: props?.identifier,
              id: props?.currentId,
              fileId: file.id,
            })
          );
          dispatch(
            removeExistingFile({
              identifier: props?.identifier,
              id: props?.currentId,
              fileId: file.id,
            })
          );
        } catch (error) {
          setFileList([...fileList]);
          message.error("Failed to delete file");
        }
      },
    });
  };
  const handleDownload = (file) => {
    try {
      if (file.url) {
        const link = document.createElement("a");
        link.href = file.url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (file.originFileObj) {
        const url = URL.createObjectURL(file.originFileObj);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download file");
    }
  };

  const handlePreview = (file) => {
    try {
      if (file.kind === "pdf") {
        const pdfWindow = window.open();
        pdfWindow.document.write(
          <iframe
            width="100%"
            height="100%"
            src="data:application/pdf;base64,${file.base64}"
          ></iframe>
        );
      } else if (["jpg", "jpeg", "png", "gif"].includes(file.kind)) {
        const imageWindow = window.open();
        imageWindow.document.write(
          <img
            src="data:image/${file.kind};base64,${file.base64}"
            style="max-width:100%; max-height:100%"
          />
        );
      } else {
        handleDownload(file);
      }
    } catch (error) {
      console.error("Preview error:", error);
      OpenNotificationWithIcon(
        "error",
        "Common.Error",
        "",
        "LeadFile.Error.FailedtoPreview"
      );
    }
  };

  const validateDocument = (file) => {
    const isLt25MB = file.size / 1024 / 1024 < 25;
    if (!isLt25MB) {
      message.warning("Each file must be smaller than 25MB.");
      return false;
    }

    if (isEmployee || !props?.multipleUpload) {
      const isAllowedType = /\.(jpg|jpeg|png|gif|pdf)$/i.test(file.name);
      if (!isAllowedType) {
        message.warning(
          intl.formatMessage({
            id: "Attachments.Validation.OnlyImage&PDF",
          })
        );
        return Upload.LIST_IGNORE;
      }
      if (!allowMultiple && fileList.length >= 1) {
        message.warning(
          intl.formatMessage({
            id: "Attachments.Validation.Onlyonefile",
          })
        );
        return Upload.LIST_IGNORE;
      }
    }

    return true;
  };

  if (props.isPreview) {
    return (
      <div>
        {fileList.map((file) => (
          <div
            key={file.uid}
            style={{
              display: "inline-block",
              alignItems: "center",
              marginTop: 8,
              background: "#f8f8f8",
              padding: "5px 10px",
              borderRadius: "5px",
              border: "1px solid #f0f0f0",
              marginRight: "10px",
            }}
          >
            <a
              href={file.url || URL.createObjectURL(file.originFileObj)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {file.name}
            </a>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(file)}
            />
            <Button
              type="link"
              icon={<DeleteOutlined />}
              onClick={() => onRemove(file)}
              danger
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {props?.screen === "SubContractor" ? (
        <Upload
          fileList={fileList}
          onChange={handleUploaderChange}
          multiple={allowMultiple}
          customRequest={() => {}}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
          onPreview={handlePreview}
          beforeUpload={validateDocument}
        >
          <Button icon={<UploadOutlined />}>
            {<FormattedMessage id="Common.UploadLimit" />}
          </Button>
        </Upload>
      ) : (
        <Upload.Dragger
          fileList={fileList}
          onChange={handleUploaderChange}
          multiple={allowMultiple}
          customRequest={() => {}}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
          onPreview={handlePreview}
          beforeUpload={validateDocument}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            {<FormattedMessage id="Attachments.DragToUpload" />}
          </p>
          <p className="ant-upload-hint">
            <FormattedMessage
              id={
                isEmployee
                  ? "Attachments.FileSupportEmployee"
                  : "Attachments.FileSupport"
              }
            />
          </p>
        </Upload.Dragger>
      )}
    </>
  );
};

export default MultiFileUploader;
